/** Team page (tenant/team.html): members, invites, seats, teams, role overview. */
export const team = {
  title: "Team",
  subtitle: "People at {{workspace}} who manage the assistants, answer handoffs and see analytics.",
  search: "Search members…",
  manageRoles: "Roles & permissions",
  members: "Members",
  all: "All",
  pending: "Pending",
  colMember: "Member",
  colRole: "Role",
  colTeams: "Teams",
  col2fa: "2FA",
  colLastActive: "Last active",
  colActions: "Actions",
  you: "(you)",
  twoFaEnabled: "Enabled",
  twoFaMissing: "Not set up",
  pendingInvite: "Pending invite",
  never: "Never",
  now: "Now",
  noTeams: "—",
  invitedBy: "Invited by {{name}} · {{date}} · expires {{expires}}",
  invitedOn: "Invited {{date}} · expires {{expires}}",
  roleSelect: "Role for {{name}}",
  ownerLocked: "Transfer ownership in Settings",
  ownRoleLocked: "You can't change your own role",
  roleChanged: "{{name}} is now {{role}}",
  multipleRoles: "{{count}} roles",
  editRoles: "Edit roles",
  remove: "Remove",
  leave: "Leave workspace",
  removeTitle: "Remove {{name}}?",
  removeBody: "They'll lose access to {{workspace}} immediately. Open handoffs assigned to them return to their team's queue. Their audit log history is kept.",
  leaveTitle: "Leave {{workspace}}?",
  leaveBody: "You'll lose access immediately. A workspace must keep at least one Owner.",
  removed: "{{name}} removed from the workspace",
  left: "You left {{workspace}}",
  cancel: "Cancel",
  emptyTitle: "No members match",
  emptyBody: "Try a different name or email.",
  emptyPendingTitle: "No pending invites",
  emptyPendingBody: "Invites you send appear here until they're accepted.",
  loadError: "Members couldn't load.",
  rolesDialogTitle: "Roles for {{name}}",
  rolesDialogBody: "Add one or more roles, each with its own scope.",
  addAssignment: "Add role",
  removeAssignment: "Remove",
  saveRoles: "Save roles",
  chooseRole: "Role",
  rolesSaved: "Roles updated for {{name}}",
  inviteMembers: "Invite members",
  viewActivity: "View activity",
  resendInvite: "Resend invite",
  inviteResent: "Invite re-sent to {{email}}",
  revokeInvite: "Revoke invite",
  inviteRevoked: "Invite for {{email}} revoked",
  inviteActions: "Actions for invite {{email}}",
  seats: {
    title: "{{used}} of {{limit}} seats used on {{plan}}",
    titleUnlimited: "{{used}} seats used on {{plan}}",
    full: "All seats are in use. New members need a free seat before they can join.",
    pendingNeedsSeat_one: "{{count}} pending invite needs a free seat before it can be accepted.",
    pendingNeedsSeat_other: "{{count}} pending invites need a free seat before they can be accepted.",
    available_one: "{{count}} seat available.",
    available_other: "{{count}} seats available.",
    meterLabel: "Seats used",
    pricePerSeat: "{{price}}/seat/month",
    pricePerSeat_year: "{{price}}/seat/year",
    addSeats: "Add seats",
    addSeatsTitle: "Add seats",
    extraSeats: "Extra seats",
    perSeat: "Per seat",
    perMonth: "{{price}} / month",
    perYear: "{{price}} / year",
    orUpgrade: "Or upgrade your plan",
    seePlans: "See plans",
    totalMonthly: "Added to your bill",
    chargedNow: "Charged now",
    chargedNowDays: "Charged now ({{days}} of {{total}} days left)",
    seatsAdded_one: "{{count}} seat added. It's prorated on your next invoice.",
    seatsAdded_other: "{{count}} seats added. They're prorated on your next invoice.",
    noBillingAccess: "Ask an Owner to add seats.",
  },
  invite: {
    title: "Invite members",
    seatWarningLead: "All {{limit}} seats are used. New members are added at",
    seatWarningTail: "prorated on your next invoice.",
    emails: "Email addresses",
    emailsPlaceholder: "name@yourorganization.com, another@yourorganization.com",
    emailsHint: "Separate with commas or new lines.",
    emailsHintDomains: "Separate with commas or new lines. Only {{domains}} addresses are allowed (Settings → Security).",
    emailsRequired: "Add at least one email address",
    emailsInvalid: "Check these addresses: {{emails}}",
    emailsTooMany: "Invite up to {{max}} people at a time",
    emailsCount_one: "{{count}} person will be invited",
    emailsCount_other: "{{count}} people will be invited",
    role: "Role",
    roleDescriptions: {
      admin: "Everything except billing and deleting the workspace.",
      editor: "Uploads documents, edits assistant instructions and answers knowledge gaps.",
      agent: "Replies to handoffs in the Inbox for their team. Can't change knowledge or settings.",
      viewer: "Read-only access to analytics and conversations, for leadership.",
      field_engineer: "Works the tickets and visits of their teams and reports from the field.",
      sales: "Works quotes and orders for client organisations and the tickets of their teams.",
      custom: "Custom role · {{count}} permissions",
    },
    team: "Team",
    noTeam: "No team",
    message: "Message",
    messagePlaceholder: "Hi! Please join our workspace so you can help answer questions.",
    send: "Send invites",
    sent_one: "Invite sent to {{count}} person. They'll get an email from {{product}}.",
    sent_other: "Invites sent to {{count}} people. They'll get an email from {{product}}.",
    skipped: "Skipped (already members or invited): {{emails}}",
    noPermission: "Ask an Owner or Admin to invite people.",
  },
  rolesCard: {
    title: "Roles & permissions",
    subtitle: "What each built-in role can do in this workspace",
    manage: "Manage roles",
    permission: "Permission",
    allowed: "Allowed",
    notAllowed: "Not allowed",
    rows: {
      billing_manage: "Manage billing",
      team_manage: "Manage team",
      knowledge_edit: "Edit knowledge",
      agents_edit: "Edit agent settings",
      inbox_reply: "View inbox & reply",
      analytics_view: "View analytics",
      api_keys_manage: "Manage API keys",
    },
    loadError: "Roles couldn't load.",
  },
  teams: {
    title: "Teams",
    newTeam: "New team",
    editTeam: "Edit {{name}}",
    membersOf: "Members of {{name}}",
    noMembers: "No members yet",
    emptyTitle: "No teams yet",
    emptyBody: "Teams route handoffs to the right people, like Front desk or Billing.",
    loadError: "Teams couldn't load.",
    routingTitle: "Handoff routing:",
    routingRule: "{{topics}} go to {{team}}",
    routingBody: "when a visitor asks for a person, the AI picks the team by topic: {{rules}}.",
    routingNone: "add topics to a team so the AI can route handoffs to it. Unrouted handoffs go to the shared inbox.",
    routing: {
      toTeam: "handoffs go to the {{name}} team.",
      toPerson: "handoffs go to {{name}}.",
      toFallback: "no team or person is set, so handoffs go to the Owner and Admins.",
      payment: "Payment questions go to the {{name}} team.",
      topicsOff: "Topic matching is off.",
      topicRules: "A team with matching topics gets the chat first: {{rules}}.",
      noTopics: "No team has routing topics, so the default target above gets every handoff.",
      queue: "A team shares one queue (the first to reply owns the chat) unless round-robin is on, which hands each chat to the member with the fewest open ones.",
    },
    traits: {
      roundRobin: "Round-robin",
      queue: "Shared queue",
      lead: "Lead: {{name}}",
      email: "Mailbox: {{email}}",
    },
    notifyEmail: "Team email",
    notifyEmailHint: "Optional. Every handoff for this team and its reminders are also sent to this address.",
    notifyEmailInvalid: "Enter a valid email address",
    lead: "Team lead",
    leadHint: "Optional. Gets a reminder when the team has not replied in time. Choose from the members above.",
    noLead: "No lead",
    confirmDeleteTitle: "Delete the {{name}} team?",
    confirmDeleteBody: "Chats waiting for this team stay in the inbox without a team, and agents that route handoffs to it fall back to the Owner and Admins. This can't be undone.",
    confirmDelete: "Delete team",
    editRouting: "Edit routing rules",
    name: "Team name",
    namePlaceholder: "e.g. Front desk",
    nameRequired: "Give the team a name",
    description: "Description",
    descriptionPlaceholder: "What this team handles",
    topics: "Route handoffs about",
    topicsPlaceholder: "billing, appointments, complaints",
    topicsHint: "Topics the AI uses to pick this team. Separate with commas.",
    members: "Members",
    membersPlaceholder: "Choose members",
    membersSearch: "Search members…",
    membersEmpty: "No matching members",
    membersSelected_one: "{{count}} member",
    membersSelected_other: "{{count}} members",
    roundRobin: "Assign handoffs round-robin",
    roundRobinHint: "Spread new handoffs evenly across team members.",
    create: "Create team",
    save: "Save team",
    created: "Team “{{name}}” created",
    saved: "Team “{{name}}” saved",
    delete: "Delete team",
    deleted: "Team “{{name}}” deleted",
    actions: "Actions for {{name}}",
  },
} as const;
